import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
  Alert
} from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import axios from 'axios';

// バックエンドと統一した定数・パターン
const MAX_NAME_LENGTH = 50;
const MIN_NAME_LENGTH = 2;
const FORBIDDEN_REGEX = /[<>"'`;\\\/*?&|^$()[\]{}:\x00-\x1F\x7F]/;

/**
 * 顧客編集ダイアログコンポーネント（顧客名のみ編集）
 */
const ClientEditDialog = ({ open, onClose, client, id }) => {
  const [name, setName] = useState('');
  const [nameError, setNameError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [apiError, setApiError] = useState('');

  // クライアントデータが変更されたときにフォームを初期化
  useEffect(() => {
    if (client && open) {
      setName(client.name || '');
      setNameError('');
      setApiError('');
    }
  }, [client, open]);

  // 顧客名のバリデーション
  const validateName = (value) => {
    if (!value || !value.trim()) {
      return "顧客名を入力してください。";
    }
    
    const trimmedValue = value.trim();
    
    if (trimmedValue.length < MIN_NAME_LENGTH) {
      return "顧客名は2文字以上で入力してください。";
    }
    
    if (trimmedValue.length > MAX_NAME_LENGTH) {
      return "顧客名は50文字以内で入力してください。";
    }
    
    if (FORBIDDEN_REGEX.test(trimmedValue)) {
      if (trimmedValue.includes("(") || trimmedValue.includes(")")) {
        return "半角括弧が含まれています。「（株）」などは全角で入力してください。";
      }
      return "使用できない文字が含まれています（< > \" ' など）。";
    }
    
    return "";
  };

  // 入力変更ハンドラー
  const handleNameChange = (e) => {
    const value = e.target.value;
    setName(value);
    
    // リアルタイムバリデーション
    if (value.trim() === "") {
      setNameError("");
    } else {
      setNameError(validateName(value));
    }
  };

  // フォーム送信
  const handleSubmit = async () => {
    // 最終バリデーション
    const trimmedName = name.trim();
    const error = validateName(trimmedName);
    
    if (error) {
      setNameError(error);
      return;
    }

    setIsSubmitting(true);
    setApiError('');

    try {
      const token = sessionStorage.getItem('token');
      
      await axios.put(
        `/api/clients/${id}`,
        {
          name: trimmedName,
          // 既存の値を保持
          address: client.address || null,
          phone: client.phone || null,
          email: client.email || null,
          note: client.note || null,
          groupIds: client.groupIds || []
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      // 成功時はダイアログを閉じてページをリロード
      onClose();
      window.location.reload();

    } catch (error) {
      let message = "顧客名の更新に失敗しました。";
      
      if (error.response?.data && typeof error.response.data === "string") {
        message = error.response.data;
      } else if (error.response?.status === 400) {
        message = "入力内容に問題があります。確認してください。";
      } else if (error.response?.status === 409) {
        message = "同じ名前の顧客が既に存在します。";
      }
      
      setApiError(message);
      setIsSubmitting(false);
    }
  };

  // ダイアログを閉じる
  const handleClose = () => {
    if (!isSubmitting) {
      setName('');
      setNameError('');
      setApiError('');
      onClose();
    }
  };

  // 送信ボタンの有効/無効判定
  const isSubmitDisabled = () => {
    const trimmedName = name.trim();
    return !trimmedName || 
           trimmedName.length < MIN_NAME_LENGTH || 
           !!nameError || 
           isSubmitting;
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose} 
      maxWidth="sm" 
      fullWidth
      PaperProps={{
        sx: { borderRadius: 2 }
      }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <PersonIcon color="primary" />
        顧客名の編集
      </DialogTitle>
      
      <DialogContent>
        <Box sx={{ pt: 2 }}>
          {apiError && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {apiError}
            </Alert>
          )}

          <TextField
            label="顧客名"
            fullWidth
            value={name}
            onChange={handleNameChange}
            error={!!nameError}
            helperText={
              nameError || 
              `${name.length}/50 文字 ${name.length < MIN_NAME_LENGTH && name.length > 0 ? '（あと' + (MIN_NAME_LENGTH - name.length) + '文字）' : ''}`
            }
            placeholder="顧客名を入力（2文字以上）"
            required
            size="small"
            InputProps={{
              startAdornment: <PersonIcon fontSize="small" sx={{ mr: 1, color: 'action.active' }} />
            }}
            sx={{
              '& .MuiInputBase-root': {
                minHeight: 48
              }
            }}
          />

          <Alert severity="info" sx={{ mt: 3 }}>
            <Typography variant="body2">
              <strong>入力のコツ：</strong><br />
              • 「（株）」「（有）」などは全角で入力してください<br />
              • 顧客名は2文字以上で入力してください<br />
              • 住所・連絡先の編集は基本情報カードから行えます
            </Typography>
          </Alert>
        </Box>
      </DialogContent>
      
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button 
          onClick={handleClose}
          disabled={isSubmitting}
        >
          キャンセル
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={isSubmitDisabled()}
          sx={{ minWidth: 80 }}
        >
          {isSubmitting ? '更新中...' : '更新'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ClientEditDialog;